import styled from '@emotion/styled';
import { useMemo, useState } from 'react';

import * as Containers from '../Containers';
import { Option, Select } from '../Select';

import { HomeyUtils } from '../../../store/HomeyUtils';

import { useDevice } from '../../../store/devices/useDevices';
import { useBaseUrl, useApi } from '../../../store/SelectedHomeyStore.hooks';

import { LiveVideoView } from './LiveVideoView';
import { SnapshotView } from './SnapshotView';

const SOURCE_TYPE = {
  SNAPSHOT: 'snapshot',
  LIVE: 'live',
} as const;

interface LiveSource {
  id: string;
  type: typeof SOURCE_TYPE.LIVE;
  title: string;
  videoId?: string;
  deviceVideoId: string;
}

interface SnapshotSource {
  id: string;
  type: typeof SOURCE_TYPE.SNAPSHOT;
  title: string;
  imageId?: string;
}

type CameraSource = LiveSource | SnapshotSource;

export interface CameraProps {
  deviceId: string;
}

export function Camera(props: CameraProps) {
  const { device } = useDevice({ deviceId: props.deviceId });
  const { baseUrl } = useBaseUrl();
  const { api } = useApi();

  const hasVideoStreaming = HomeyUtils.getHasVideoStreaming(api?.homey);

  // Build combined list of camera sources (snapshots and live videos)
  const cameraSources = useMemo<CameraSource[]>(() => {
    if (device == null) {
      return [];
    }

    const sources: CameraSource[] = [];

    // Add live video sources first (preferred) - only if Homey supports video streaming
    if (hasVideoStreaming) {
      const deviceVideos = device.videos?.filter((video) => video.type === 'camera') ?? [];
      deviceVideos.forEach((video) => {
        sources.push({
          id: `live:${video.videoObj?.id}`,
          type: SOURCE_TYPE.LIVE,
          title: video.title,
          videoId: video.videoObj?.id,
          deviceVideoId: video.id,
        });
      });
    }

    // Add snapshot sources
    const deviceImages = device.images?.filter((image) => image.type === 'camera') ?? [];
    deviceImages.forEach((image) => {
      sources.push({
        id: `snapshot:${image.imageObj?.id}`,
        type: SOURCE_TYPE.SNAPSHOT,
        title: image.title,
        imageId: image.imageObj?.id,
      });
    });

    return sources;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only recompute when images/videos change
  }, [device?.images, device?.videos, hasVideoStreaming]);

  // Track user's selection (may become invalid when sources change)
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);

  // Derive valid selection - fall back to first source if current selection is invalid
  const isSelectionValid = cameraSources.some(
    (cameraSource) => cameraSource.id === selectedSourceId,
  );
  const effectiveSourceId = isSelectionValid ? selectedSourceId : (cameraSources[0]?.id ?? null);
  const selectedSource = cameraSources.find(
    (cameraSource) => cameraSource.id === effectiveSourceId,
  );

  return (
    <Containers.Control>
      <Containers.Select>
        {cameraSources.length > 1 && (
          <S.SelectWrapper>
            <Select
              aria-label="Camera Select"
              // @ts-expect-error Select component types
              selectedKey={effectiveSourceId}
              onSelectionChange={(key: string | null) => {
                setSelectedSourceId(key);
              }}
            >
              {cameraSources.map((source) => (
                <Option key={source.id}>{source.title}</Option>
              ))}
            </Select>
          </S.SelectWrapper>
        )}
      </Containers.Select>

      <Containers.Action>
        {selectedSource?.type === SOURCE_TYPE.LIVE ? (
          <LiveVideoView device={device} baseUrl={baseUrl} source={selectedSource} />
        ) : selectedSource?.type === SOURCE_TYPE.SNAPSHOT ? (
          <SnapshotView baseUrl={baseUrl} source={selectedSource} />
        ) : null}
      </Containers.Action>
    </Containers.Control>
  );
}

function S() {}
Camera.S = S;

S.SelectWrapper = styled.div`
  button > div:first-of-type {
    text-transform: none;
  }
`;
